/**
 * Catalog merge — pure, verbatim from source allInts() + nameOf().
 * Combines built-in INTS with custom in-directory adapters and applies
 * per-connector metaOverrides on top.
 */
import type { Adapter, Integration } from './types';
import { INTS } from '../data/seeds';

export type MetaOverride = {
  name?: string;
  company?: string;
  category?: string;
  change?: Integration['change'];
  purpose?: string;
};

/** Merge built-ins + custom adapters, apply metaOverrides. */
export function allInts(
  adapters: Adapter[],
  metaOverrides: Record<string, MetaOverride>,
): Integration[] {
  const extra: Integration[] = adapters
    .filter(a => a.inDirectory && !INTS.some(i => i.id === a.id))
    .map(a => ({
      id: a.id,
      name: a.name,
      purpose: a.purpose || 'Newly ingested adapter.',
      cls: a.cls,
      color: a.color,
      initials: a.initials,
      versions: [a.version],
      change: a.change || 'poll',
      products: a.products || [{ key: 'CRM', dataType: 'contacts' }],
      category: a.category || 'Custom',
      company: a.company || a.name,
      _custom: true,
      _published: !!a.approved,
    }));

  const ov = metaOverrides || {};
  return [...INTS, ...extra].map(i => {
    const o = ov[i.id];
    if (!o) return i;
    const name = o.name != null ? o.name : i.name;
    return {
      ...i,
      name,
      company: o.company != null ? o.company : o.name != null ? name : i.company,
      category: o.category != null ? o.category : i.category,
      change: o.change != null ? o.change : i.change,
      purpose: o.purpose != null ? o.purpose : i.purpose,
    };
  });
}

/** Display name for a connector id. */
export function nameOf(id: string, ints: Integration[]): string {
  const i = ints.find(x => x.id === id);
  return i ? i.name : id;
}
